package com.veldekar.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight.Companion.Bold
import com.varabyte.kobweb.compose.css.FontWeight.Companion.Bolder
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment.TopEnd
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.overflowWrap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobwebx.markdown.markdown
import com.veldekar.components.widgets.BookmarkMenu
import com.veldekar.components.widgets.IconButton
import com.veldekar.data.TimelineData
import com.veldekar.toSitePalette
import kotlinx.browser.window
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

val MarkdownStyle = CssStyle {
    base { Modifier.fillMaxSize() }

    cssRule("h1") {
        Modifier
            .fontSize(3.cssRem)
            .fontWeight(400)
            .margin(bottom = 2.5.cssRem)
            .lineHeight(1.2)
    }

    cssRule("h2") {
        Modifier
            .fontSize(3.cssRem)
            .fontWeight(300)
            .margin(topBottom = 2.cssRem)
    }

    cssRule("h3") {
        Modifier
            .fontSize(2.4.cssRem)
            .fontWeight(300)
            .margin(topBottom = 1.5.cssRem)
    }

    cssRule("h4") {
        Modifier
            .fontSize(1.2.cssRem)
            .fontWeight(Bolder)
            .margin(top = 1.cssRem, bottom = 0.5.cssRem)
    }

    cssRule("ul") {
        Modifier.fillMaxWidth().overflowWrap(OverflowWrap.BreakWord)
    }

    cssRule(" :is(li,ol,ul)") {
        Modifier.margin(bottom = 0.25.cssRem)
    }

    cssRule("code") {
        Modifier
            .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
            .fontWeight(Bolder)
    }

    cssRule("pre") {
        Modifier
            .margin(top = 0.5.cssRem, bottom = 2.cssRem)
            .fillMaxWidth()
    }
    cssRule("pre > code") {
        Modifier
            .display(DisplayStyle.Block)
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().nearBackground)
            .border(1.px, Solid, colorMode.toPalette().color)
            .borderRadius(0.25.cssRem)
            .padding(0.5.cssRem)
            .fontSize(1.cssRem)
            .overflow { x(Overflow.Auto) }
    }
}

val AboutMarkdownStyle = CssStyle {
    base {
        Modifier.fillMaxSize()
    }

    cssRule("table") {
        Modifier
            .fillMaxWidth()
            .margin(topBottom = 1.5.cssRem)
            .styleModifier {
                property("border-collapse", "collapse")
            }
    }

    cssRule("thead") {
        Modifier
            .borderBottom(2.px, Solid, colorMode.toPalette().color.toRgb().copyf(alpha = 0.2f))
    }

    cssRule("th") {
        Modifier
            .padding(0.75.cssRem)
            .styleModifier {
                property("text-align", "left")
            }
            .fontWeight(Bold)
    }

    cssRule("td") {
        Modifier
            .padding(0.75.cssRem)
            .borderBottom(1.px, Solid, colorMode.toPalette().color.toRgb().copyf(alpha = 0.1f))
    }

    cssRule("tbody tr:nth-child(even)") {
        Modifier.backgroundColor(colorMode.toPalette().color.toRgb().copyf(alpha = 0.05f))
    }
}

val LogMarkdownStyle = CssStyle {
    base {
        Modifier.fillMaxSize()
    }

    cssRule("h2") {
        Modifier
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(5)
            .backgroundColor(colorMode.toPalette().background.toRgb().copyf(alpha = 0.95f))
            .styleModifier {
                property("backdrop-filter", "blur(12px)")
                property("-webkit-backdrop-filter", "blur(12px)")
            }
            .padding(topBottom = 0.75.cssRem, leftRight = 1.cssRem)
            .padding(right = 4.cssRem)
            .margin(top = 0.cssRem, bottom = 1.5.cssRem)
            .fontSize(1.5.cssRem)
            .fontWeight(Bold)
    }

    cssRule("img") {
        Modifier
            .maxWidth(100.percent)
            .height(auto)
            .display(DisplayStyle.Block)
    }

    cssRule("p > img") {
        Modifier.margin(topBottom = 0.5.cssRem)
    }

    cssRule("video") {
        Modifier
            .maxWidth(100.percent)
            .height(auto)
            .display(DisplayStyle.Block)
    }
}

@InitRoute
fun initMarkdownLayouts(ctx: InitRouteContext) {
    val markdown = ctx.markdown!!
    val title = markdown.frontMatter["title"]?.singleOrNull()
    require(title != null) { "Markdown file must set \"title\" in frontmatter" }

    val isoDate = markdown.frontMatter["isoDate"]?.singleOrNull()
    val date = isoDate?.let { LocalDate.parse(it) }
    val ogTitle = markdown.frontMatter["ogTitle"]?.singleOrNull()
    val ogDescription = markdown.frontMatter["ogDescription"]?.singleOrNull()
    val ogImage = markdown.frontMatter["ogImage"]?.singleOrNull()
    val ogImageWidth = markdown.frontMatter["ogImageWidth"]?.singleOrNull()?.toIntOrNull()
    val ogImageHeight = markdown.frontMatter["ogImageHeight"]?.singleOrNull()?.toIntOrNull()
    val ogImageAlt = markdown.frontMatter["ogImageAlt"]?.singleOrNull()
    val isModal = markdown.frontMatter["isModal"]?.singleOrNull()?.toBoolean() ?: false

    ctx.data.add(PageLayoutData(title, date, ogTitle, ogDescription, ogImage, ogImageWidth, ogImageHeight, ogImageAlt, isModal = isModal))
}

@Composable
fun LogPageContent(content: @Composable () -> Unit) {
    Div(attrs = listOf(MarkdownStyle, LogMarkdownStyle).toAttrs()) {
        content()
    }
}

@Composable
fun AllSessionsContent(title: String, content: @Composable () -> Unit) {
    Div(attrs = listOf(MarkdownStyle, LogMarkdownStyle).toAttrs()) {
        H1 { Text(title) }

        SessionLogControls()

        content()
    }
}

@Composable
private fun SessionLogControls() {
    var openMenu by remember { mutableStateOf<String?>(null) }
    var showBackToTop by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        window.onscroll = {
            val shouldShow = window.scrollY > 200
            if (showBackToTop != shouldShow) {
                showBackToTop = shouldShow
            }
            null
        }
    }

    Box(
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(20)
            .padding(right = 1.cssRem, top = 0.4.cssRem)
            .styleModifier { property("pointer-events", "none") },
        contentAlignment = TopEnd
    ) {
        Row(Modifier.styleModifier { property("pointer-events", "auto") }) {
            if (showBackToTop) {
                IconButton(
                    onClick = { window.scrollTo(0.0, 0.0) },
                    ariaLabel = "Back to Top"
                ) {
                    SpanText("⬆️")
                }
            }

            TimelineData.categories.forEach { category ->
                BookmarkMenu(
                    icon = category.icon,
                    title = category.title,
                    events = category.bookmarks,
                    isOpen = openMenu == category.title,
                    onToggle = { openMenu = if (openMenu == category.title) null else category.title },
                    onClose = { openMenu = null }
                )
            }
        }
    }
}

@Composable
@Layout(".components.layouts.PageLayout")
fun AboutMarkdownLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    Div(attrs = listOf(MarkdownStyle, AboutMarkdownStyle).toAttrs()) {
        H1 { Text(data.title) }
        content()
    }
}
